import { useState, createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import ContentEditable from 'react-contenteditable';
import sanitizeHtml from 'sanitize-html';
import classNames from 'classnames';
import IconButton from './IconButton';

const EditBox = ({
  html,
  allowedTags,
  allowedAttributes,
  placeholder,
  className,
  submitOnEnter,
  submitOnCtrlEnter,
  withButton,
  onSubmit,
}) => {
  const [contentHtml, setContentHtml] = useState(html);

  const textRef = createRef();

  useEffect(() => {
    const keypressHandler = (event) => {
      if (contentHtml && submitOnCtrlEnter && event.metaKey && event.key === 'Enter') {
        onSubmit(contentHtml);
      }
      
      if (contentHtml && submitOnEnter && event.key === 'Enter') {
        event.preventDefault();
        onSubmit(contentHtml);
      }
    };

    if (submitOnEnter || submitOnCtrlEnter) {
      textRef.current && textRef.current.el.current.addEventListener('keydown', keypressHandler);
    }

    return () => {
      if (submitOnEnter || submitOnCtrlEnter) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        textRef.current?.el?.current.removeEventListener('keypress', keypressHandler);
      }
    }
  }, [textRef, submitOnEnter, submitOnCtrlEnter, onSubmit, contentHtml]);

  const onContentChange = (event) => {
		const sanitizeConf = {
			allowedTags: allowedTags || [],
      allowedAttributes: allowedAttributes || {},
		};
    
    const html = sanitizeHtml(event.currentTarget.innerHTML, sanitizeConf);
    setContentHtml(html);
	};

  const onBlur = (event) => {
    onContentChange(event);
  };

  return (
    <Wrapper className={className} withButton={withButton}>
      <ContentEditable
        ref={textRef}
        data-placeholder={placeholder}
        className={classNames({
          empty: !contentHtml.length
        })}
        onChange={onContentChange}
        onBlur={onBlur}
        html={contentHtml}
      />
      {withButton && (
        <IconButton
          disabled={!contentHtml.length}
          onClick={() => onSubmit(contentHtml)}
          icon={withButton}
        />
      )}
    </Wrapper>
  );
}

export default styled(EditBox)``;

EditBox.propTypes = {
  html: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  allowedTags: PropTypes.array,
  allowedAttributes: PropTypes.shape(PropTypes.any),
  submitOnEnter: PropTypes.bool,
  submitOnCtrlEnter: PropTypes.bool,
  withButton: PropTypes.string,
  onSubmit: PropTypes.func,
};

EditBox.defaultProps = {
  html: '',
  placeholder: '',
  onSubmit: () => {},
};

const Wrapper = styled.div`
  width: 100%;
  position: relative;

  & > [contenteditable="true"] {
    width: 100%;
    color: #000;
    min-height: 23px;
    background: var(--lizzy-black-5);
    border-radius: 5px;
    padding: 2px 8px;
    white-space: pre-wrap;

    &:focus-visible {
      outline: 0;
    }

    &.empty {
      &::before {
        position: absolute;
        content: attr(data-placeholder);
        color: var(--lizzy-black-35);
      }
    }
  }

  ${IconButton} {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);

    svg.lizzy-icon {
      color: var(--lizzy-black-65);
      width: 14px;
      height: 14px;
      margin: 4px;
    }
  }

  ${p => p.withButton && css`
    & > [contenteditable="true"] {
      padding-right: 24px;
    }
  `}
`;